import {
  CLIENT_PROJECT_ORGANISATION_REFUSALS,
  ClientProjectOrganisation,
  ClientProjectOrganisationRefusal,
} from "@/data/models/ClientAdministrationRow";
import { ClientSetupProjectOrganisation, ProjectOrganisationApp } from "@/data/models/ClientSetup";

/**
 * The Projektorganisation logic (the four "... ausführen?" answers) shared by the guided setup (#2610082)
 * and the Projektorganisation card (2026-10-29), which replaced IRES Office's own settings section. Pure,
 * and a leaf: the client detail, the setup and the Verlauf all import it, so it imports none of them.
 * IRES Cloud's `setClientProjectOrganisationForAdministration` is the authority.
 */

type Translate = (key: string, values?: unknown[]) => string;

/** The four Projektorganisation questions, in the order IRES Office's settings page asked them. */
export const PROJECT_ORGANISATION_QUESTIONS: Array<keyof ClientSetupProjectOrganisation> = [
  "newProjectsIn",
  "editProjectsIn",
  "newAppointmentsIn",
  "finaliseProjectsIn",
];

/** The questions that may be answered "nowhere", as the settings page offered it ("Nie"). */
export const MAY_BE_NOWHERE: Array<keyof ClientSetupProjectOrganisation> = ["newProjectsIn", "editProjectsIn"];

/** A Projektorganisation choice by the name IRES Office's settings page gave it. */
export function projectSettingsOptionText(option: ProjectOrganisationApp, t: Translate): string {
  switch (option) {
    case "PORTAL":
      return t("common.portal");
    case "PROJECT_MANAGER":
      return t("common.projectManager");
    case "DEVICE_MANAGER":
      return t("common.deviceManager");
    case "API":
      return t("common.api");
    default:
      return t("common.never");
  }
}

/** A question's answer: its apps, or "Nie" for none. */
export function projectOrganisationText(options: ProjectOrganisationApp[] | undefined, t: Translate): string {
  return options && options.length > 0 ? options.map((option) => projectSettingsOptionText(option, t)).join(", ") : t("common.never");
}

/** A copy of the four answers, so the dialog's form never shares a list with the loaded value. */
export function copyProjectOrganisation(value: ClientSetupProjectOrganisation): ClientSetupProjectOrganisation {
  return {
    newProjectsIn: [...value.newProjectsIn],
    editProjectsIn: [...value.editProjectsIn],
    finaliseProjectsIn: [...value.finaliseProjectsIn],
    newAppointmentsIn: [...value.newAppointmentsIn],
  };
}

function sameApps(left: ProjectOrganisationApp[], right: ProjectOrganisationApp[]): boolean {
  return left.length === right.length && left.every((app) => right.includes(app));
}

/** Whether two sets of answers differ in any question, ignoring order. */
export function projectOrganisationDiffers(left: ClientSetupProjectOrganisation, right: ClientSetupProjectOrganisation): boolean {
  return PROJECT_ORGANISATION_QUESTIONS.some((question) => !sameApps(left[question], right[question]));
}

/** Whether a question may stay without an app: new and edited projects may be Nie, the others need one. */
export function mayBeNowhere(question: keyof ClientSetupProjectOrganisation): boolean {
  return MAY_BE_NOWHERE.includes(question);
}

/**
 * The apps a question offers in the dialog: the ones the products offer, then any the question already
 * has that they no longer offer (it may be taken away, never added), as IRES Cloud allows them.
 */
export function questionApps(organisation: ClientProjectOrganisation, question: keyof ClientSetupProjectOrganisation): ProjectOrganisationApp[] {
  const kept = organisation.value[question].filter((app) => !organisation.offered.includes(app));
  return [...organisation.offered, ...kept];
}

/** The dialog may save when something changed and every question that needs an app has one. */
export function canSaveProjectOrganisation(stored: ClientSetupProjectOrganisation, form: ClientSetupProjectOrganisation): boolean {
  return (
    projectOrganisationDiffers(stored, form) &&
    PROJECT_ORGANISATION_QUESTIONS.every((question) => mayBeNowhere(question) || form[question].length > 0)
  );
}

/** IRES Cloud's refusal reason in a failed call, else null. */
export function clientProjectOrganisationRefusalOf(error: unknown): ClientProjectOrganisationRefusal | null {
  const reason = (error as { details?: { reason?: unknown } } | null)?.details?.reason;
  return typeof reason === "string" && CLIENT_PROJECT_ORGANISATION_REFUSALS.includes(reason as ClientProjectOrganisationRefusal)
    ? (reason as ClientProjectOrganisationRefusal)
    : null;
}

/** The i18n key of a refusal from IRES Cloud, by its `details.reason`; a general failure otherwise. */
export function clientProjectOrganisationErrorKey(error: unknown): string {
  const reason = clientProjectOrganisationRefusalOf(error);
  return reason ? `admin.clients.projectOrganisation.errors.${reason}` : "admin.clients.projectOrganisation.errors.FAILED";
}
